import type {
  BaseRecord,
  CrudFilters,
  DataProvider,
  GetListParams,
  GetOneParams,
} from '@refinedev/core'

import {
  dealerBackofficeListSalesHistory,
  ordersGetFulfillmentOrder,
  type FulfillmentOrderDisplayStatusRoot,
  type SalesHistoryDateGroupRoot,
} from '@repo/sdk'

import { i18n } from '../shared/i18n'

import { handleSdkError } from './sdkErrorUtils'
import { getApiUrl } from '../lib/runtime-env'

function extractDisplayStatus(
  filters?: CrudFilters,
): FulfillmentOrderDisplayStatusRoot | undefined {
  if (!filters) return undefined
  for (const filter of filters) {
    if ('field' in filter && filter.field === 'displayStatus' && 'value' in filter) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- filter value is displayStatus string
      return filter.value as FulfillmentOrderDisplayStatusRoot
    }
  }
  return undefined
}

/**
 * Safety net for the 신규주문 (PAID) fetch-all loop below: a server that keeps answering
 * `hasNext: true` would otherwise spin forever and freeze the browser tab.
 */
const MAX_SALES_HISTORY_PAGES = 50

function listBuyerIds(dateGroups: readonly SalesHistoryDateGroupRoot[]): string[] {
  const buyerIds: string[] = []
  const seen = new Set<string>()
  for (const dateGroup of dateGroups) {
    for (const broadcastGroup of dateGroup.broadcastGroups) {
      for (const fulfillmentOrder of broadcastGroup.fulfillmentOrders) {
        const buyerId = fulfillmentOrder.buyer.userId
        if (seen.has(buyerId)) continue
        seen.add(buyerId)
        buyerIds.push(buyerId)
      }
    }
  }
  return buyerIds
}

/**
 * Keeps only the fulfillment orders of `buyerIds`, preserving the date > broadcast shape.
 *
 * 신규주문 paginates by buyer, but the API paginates by fulfillment order, so we fetch every
 * page and slice the grouped buyers here. Without this, `total` counted buyers while the rows
 * were never sliced and page 2 showed exactly the same content as page 1.
 */
function keepBuyers(
  dateGroups: readonly SalesHistoryDateGroupRoot[],
  buyerIds: readonly string[],
): SalesHistoryDateGroupRoot[] {
  const wanted = new Set(buyerIds)
  const result: SalesHistoryDateGroupRoot[] = []
  for (const dateGroup of dateGroups) {
    const broadcastGroups = dateGroup.broadcastGroups
      .map((broadcastGroup) => ({
        ...broadcastGroup,
        fulfillmentOrders: broadcastGroup.fulfillmentOrders.filter((fulfillmentOrder) =>
          wanted.has(fulfillmentOrder.buyer.userId),
        ),
      }))
      .filter((broadcastGroup) => broadcastGroup.fulfillmentOrders.length > 0)
    if (broadcastGroups.length === 0) continue
    result.push({
      ...dateGroup,
      fulfillmentOrderCount: broadcastGroups.reduce(
        (sum, broadcastGroup) => sum + broadcastGroup.fulfillmentOrders.length,
        0,
      ),
      broadcastGroups,
    })
  }
  return result
}

function notImplemented(): never {
  throw new Error(i18n.t('common.errors.notImplemented'))
}

export const orderListDataProvider: DataProvider = {
  getList: async <TData extends BaseRecord = BaseRecord>(params: GetListParams) => {
    try {
      const page = params.pagination?.currentPage ?? 1
      const pageSize = params.pagination?.pageSize ?? 20
      const displayStatus = extractDisplayStatus(params.filters)

      const isBuyerGroupedNewOrders = displayStatus === 'PAID'
      const response = await dealerBackofficeListSalesHistory({
        query: { page: isBuyerGroupedNewOrders ? 1 : page, pageSize, displayStatus },
      })
      const dateGroups = [...(response.data?.data ?? [])]
      if (!isBuyerGroupedNewOrders) {
        return {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- SDK response matches TData structure
          data: dateGroups as unknown as TData[],
          total: response.data?.meta.totalItems ?? 0,
        }
      }

      let nextPage = 2
      let hasNext = response.data?.meta.hasNext ?? false
      while (hasNext && nextPage <= MAX_SALES_HISTORY_PAGES) {
        const nextResponse = await dealerBackofficeListSalesHistory({
          query: { page: nextPage, pageSize, displayStatus },
        })
        dateGroups.push(...(nextResponse.data?.data ?? []))
        hasNext = nextResponse.data?.meta.hasNext ?? false
        nextPage += 1
      }

      // Interim fix: 신규주문 is paginated by buyer on the client until the API can group by buyer.
      const buyerIds = listBuyerIds(dateGroups)
      const pageBuyerIds = buyerIds.slice((page - 1) * pageSize, page * pageSize)

      return {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- SDK response matches TData structure
        data: keepBuyers(dateGroups, pageBuyerIds) as unknown as TData[],
        total: buyerIds.length,
      }
    } catch (error_) {
      throw handleSdkError(error_)
    }
  },

  getOne: async <TData extends BaseRecord = BaseRecord>(params: GetOneParams) => {
    try {
      const id = String(params.id)
      const response = await ordersGetFulfillmentOrder({
        path: { fulfillmentOrderId: id },
      })

      if (!response.data) {
        throw new Error(i18n.t('common.errors.detailNotFound'))
      }

      return {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- SDK response matches TData structure
        data: response.data as unknown as TData,
      }
    } catch (error_) {
      throw handleSdkError(error_)
    }
  },

  create: notImplemented,
  update: notImplemented,
  deleteOne: notImplemented,
  getApiUrl,
}
